import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { SubscriptionPlan } from '../services/api';
import { getLaunchAnchor } from '../constants/pricing';

interface PricingCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  onSelect: (plan: SubscriptionPlan) => void;
  isLoading?: boolean;
  billingAnnual?: boolean;
  fanFavorite?: boolean;
  // Marketing mode (public landing page): the card is a pure plan-info preview
  // with a single CTA that registers for a free trial instead of opening
  // checkout. No "Subscribe" button, no current-plan/disabled states.
  marketing?: boolean;
  // Explicit `| undefined` unions: PricingPage forwards these via JSX prop
  // spread where the value may be undefined, which `exactOptionalPropertyTypes`
  // rejects against a bare optional. See tsconfig exactOptionalPropertyTypes.
  onMarketingCta?: (() => void) | undefined;
  marketingCtaLabel?: string | undefined;
}

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  isCurrentPlan,
  onSelect,
  isLoading,
  billingAnnual = false,
  fanFavorite = false,
  marketing = false,
  onMarketingCta,
  marketingCtaLabel,
}) => {
  const { t } = useTranslation();
  const isFree = plan.id === 'free';
  const isPro = plan.id === 'pro';

  const formatFeatures = (features: string[] | string) => {
    if (Array.isArray(features)) return features;
    try {
      const featureList = JSON.parse(features || '[]');
      return Array.isArray(featureList) ? featureList : [];
    } catch {
      return [];
    }
  };

  const features = formatFeatures(plan.features);

  const yearly = plan.yearlyPrice ?? 0;
  const showAnnual = billingAnnual && yearly > 0 && !isFree;
  const monthlyEquiv = showAnnual && yearly > 0 ? yearly / 12 : null;
  const savePct =
    showAnnual && plan.monthlyPrice > 0 && yearly > 0
      ? Math.round((1 - yearly / (plan.monthlyPrice * 12)) * 100)
      : null;

  const highlight = fanFavorite || isPro;

  // Launch-price anchor (Billing #2 PR-C1): struck-through "regular" price shown
  // above the live price. Paid plans only AND only when an anchor exists for this
  // plan's billing code. Distinct lever from the annual Save% above. The live
  // price shown depends on the monthly/annual toggle, so anchor against the same.
  const livePrice = showAnnual ? yearly : plan.monthlyPrice;
  // Resolve the launch anchor against the SELECTED billing interval. The plan
  // catalog uses a single `*_monthly` code with a yearlyPrice, so when the
  // annual toggle is on we look up the `*_yearly` anchor instead.
  const anchorCode = !isFree
    ? showAnnual
      ? plan.code?.replace(/_monthly$/, '_yearly')
      : plan.code
    : undefined;
  const launchAnchor = anchorCode ? getLaunchAnchor(anchorCode) : null;
  const launchSavePct =
    launchAnchor && launchAnchor.regular > 0 && livePrice > 0 && livePrice < launchAnchor.regular
      ? Math.round((1 - livePrice / launchAnchor.regular) * 100)
      : null;
  const showLaunchAnchor = launchAnchor != null && launchSavePct != null;
  const launchRegularText = launchAnchor
    ? launchAnchor.regular.toFixed(showAnnual ? 0 : 2)
    : '';
  // `{{price}}` is i18next interpolation, not a JS template literal; the
  // no-template-curly-in-string rule false-positives on the adjacent `${{`.
  // eslint-disable-next-line no-template-curly-in-string
  const regularPriceLabel = launchAnchor ? t('pricing.launchRegular', 'Regular price ${{price}}', { price: launchRegularText }) : undefined;

  return (
    <div
      className={`pricing-card-institutional h-100 d-flex flex-column position-relative rounded-xl overflow-hidden ${highlight ? 'is-highlight' : ''} ${isCurrentPlan ? 'is-current' : ''}`}
      style={{
        border: highlight ? '1px solid rgba(242, 202, 80, 0.45)' : '1px solid var(--border-ghost)',
        backgroundColor: highlight ? 'var(--surface_container_high)' : 'var(--surface_container)',
        cursor: isCurrentPlan && !marketing ? 'default' : 'pointer',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
      }}
      onClick={() => {
        if (marketing) {
          onMarketingCta?.();
          return;
        }
        if (!isCurrentPlan && !isLoading) onSelect(plan);
      }}
      role="article"
      aria-label={`${plan.name} plan`}
    >
      {fanFavorite && (
        <div
          className="text-center py-1 small fw-semibold text-uppercase"
          style={{
            background: 'linear-gradient(90deg, var(--accent-primary), var(--primary_container))',
            color: 'var(--on_primary)',
            letterSpacing: '0.06em',
            fontSize: '0.65rem',
          }}
        >
          Fan favorite
        </div>
      )}

      <div className="p-4 d-flex flex-column flex-grow-1">
        <div className="text-center mb-4">
          <h3 className="font-display text-xl text-text-primary mb-2">{plan.name}</h3>

          {/* Marketing mode: a single clean value line instead of the full
              monthly/annual price chrome + launch anchor. No billing toggle,
              no struck prices - this card only registers a free trial. */}
          {marketing && (
            <>
              <div className="d-flex align-items-baseline justify-content-center gap-1 mt-2 mb-1">
                <span className="display-6 fw-bold" style={{ color: 'var(--accent-primary)' }}>
                  {t('pricing.marketingPriceFree', 'Free')}
                </span>
                <span className="text-text-muted">
                  {t('pricing.marketingPriceForDays', 'for {{days}} days', {
                    days: plan.trialDays || 14,
                  })}
                </span>
              </div>
              {plan.monthlyPrice > 0 && (
                <div className="small text-text-secondary">
                  {t('pricing.marketingThenPerMonth', 'then ${{price}}/mo - cancel anytime', {
                    price: plan.monthlyPrice.toFixed(2),
                  })}
                </div>
              )}
            </>
          )}

          {!marketing && showLaunchAnchor && launchAnchor && (
            <div className="d-flex align-items-center justify-content-center gap-2 mb-1 flex-wrap">
              <span
                className="small text-text-muted"
                style={{ textDecoration: 'line-through' }}
                aria-label={regularPriceLabel}
              >
                ${launchAnchor.regular.toFixed(showAnnual ? 0 : 2)}
                {showAnnual ? '/yr' : '/mo'}
              </span>
              <span
                className="badge rounded-pill fw-semibold"
                style={{
                  backgroundColor: 'var(--accent-bearish-muted)',
                  color: 'var(--accent-bearish)',
                  border: '1px solid var(--accent-bearish)',
                  fontSize: '0.62rem',
                  letterSpacing: '0.02em',
                }}
              >
                {t('pricing.launchSave', 'Launch price · save {{pct}}%', {
                  pct: launchSavePct,
                })}
              </span>
            </div>
          )}

          {!marketing && (
            <div className="d-flex align-items-baseline justify-content-center gap-1 flex-wrap">
              {isFree ? (
                <span className="display-6 fw-bold text-text-primary">$0</span>
              ) : showAnnual ? (
                <>
                  <span className="display-6 fw-bold" style={{ color: 'var(--accent-primary)' }}>
                    ${yearly.toFixed(0)}
                  </span>
                  <span className="text-text-muted">/yr</span>
                </>
              ) : (
                <>
                  <span className="display-6 fw-bold" style={{ color: 'var(--accent-primary)' }}>
                    ${plan.monthlyPrice.toFixed(2)}
                  </span>
                  <span className="text-text-muted">/mo</span>
                </>
              )}
            </div>
          )}

          {!marketing && showAnnual && monthlyEquiv != null && (
            <div className="small text-text-secondary mt-1">
              ≈ ${monthlyEquiv.toFixed(2)}/mo billed annually
              {savePct != null && savePct > 0 && (
                <span className="ms-2" style={{ color: 'var(--accent-bullish)' }}>
                  Save ~{savePct}%
                </span>
              )}
            </div>
          )}

          {!marketing && !showAnnual && !isFree && yearly > 0 && (
            <div className="small text-text-muted mt-1">
              or ${yearly.toFixed(0)}/yr
              {plan.monthlyPrice > 0 && (
                <span className="ms-1">
                  (~{Math.round((1 - yearly / (plan.monthlyPrice * 12)) * 100)}% off)
                </span>
              )}
            </div>
          )}

          {!marketing && plan.trialDays > 0 && !isFree && (
            <div className="small mt-2 fw-medium" style={{ color: 'var(--primary_container)' }}>
              {plan.trialDays}-day trial on paid checkout
            </div>
          )}
        </div>

        <ul className="list-unstyled flex-grow-1 mb-4" style={{ padding: 0 }}>
          {features.map((feature: string, index: number) => (
            <li
              key={index}
              className="d-flex align-items-start gap-2 py-2"
              style={{
                borderBottom: index < features.length - 1 ? '1px solid var(--border-ghost)' : 'none',
              }}
            >
              <Check className="flex-shrink-0 mt-0.5" size={16} style={{ color: 'var(--accent-primary)' }} aria-hidden />
              <span className="small text-text-secondary" style={{ lineHeight: 1.45 }}>
                {feature}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          data-testid={marketing ? 'pricing-marketing-cta' : undefined}
          className="btn w-100 fw-semibold rounded-lg py-2 border-0"
          onClick={(e) => {
            e.stopPropagation();
            if (marketing) {
              onMarketingCta?.();
              return;
            }
            if (!isCurrentPlan && !isLoading) onSelect(plan);
          }}
          disabled={!marketing && (isCurrentPlan || isLoading)}
          style={
            !marketing && isCurrentPlan
              ? {
                  backgroundColor: 'var(--surface_container_lowest)',
                  color: 'var(--text-muted)',
                }
              : !marketing && isFree
                ? {
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-ghost)',
                  }
                : {
                    background: 'linear-gradient(135deg, var(--accent-primary), var(--primary_container))',
                    color: 'var(--on_primary)',
                  }
          }
        >
          {marketing
            ? marketingCtaLabel ||
              t('pricing.ctaStartTrial', 'Start {{days}}-day trial', {
                days: plan.trialDays || 14,
              })
            : isLoading
              ? 'Please wait…'
              : isCurrentPlan
                ? 'Current plan'
                : isFree
                  ? 'Get started free'
                  : plan.trialDays > 0
                    ? t('pricing.ctaStartTrial', 'Start {{days}}-day trial', {
                        days: plan.trialDays,
                      })
                    : t('pricing.ctaSubscribe', 'Subscribe to Pro')}
        </button>
      </div>
    </div>
  );
};

export default PricingCard;
